import { useCallback, useRef } from "react";
import { useAppContext } from "@/context/AppContext";
import { useLibraryContext } from "@/context/LibraryContext";
import { getFetchErrorMessage } from "@/hooks/useFetchVraag";

export function useZelfUitwerkenAccessGate() {
  const { api } = useAppContext();
  const {
    currentVraag,
    currentSubvraag,
    currentStappenplan,
    setCurrentVraag,
    setCurrentStappenplan,
    setFetchError,
    setFetchErrorMessage,
    setPaymentRequired,
    setRateLimited,
    shareToken
  } = useLibraryContext();
  const currentSubvraagRef = useRef(currentSubvraag);
  currentSubvraagRef.current = currentSubvraag;

  const markCurrentSubvraagAccess = useCallback(
    (subvraagId: number, hasAccess: boolean) => {
      setCurrentVraag((vraag) => {
        if (!vraag) return vraag;
        const target = vraag.subvragen.find(
          (subvraag) => subvraag.id === subvraagId
        );
        if (!target || target.has_access === hasAccess) return vraag;
        return {
          ...vraag,
          subvragen: vraag.subvragen.map((subvraag) =>
            subvraag.id === subvraagId
              ? { ...subvraag, has_access: hasAccess }
              : subvraag
          )
        };
      });
    },
    [setCurrentVraag]
  );

  return useCallback(async () => {
    if (!currentSubvraag) return false;

    if (
      currentVraag?.code === "demo" ||
      currentSubvraag.id < 0 ||
      currentSubvraag.stappenplan_id < 0
    ) {
      return true;
    }

    if (currentStappenplan?.id === currentSubvraag.stappenplan_id) {
      markCurrentSubvraagAccess(currentSubvraag.id, true);
      setPaymentRequired(false);
      return true;
    }

    const requestSubvraagId = currentSubvraag.id;
    const requestStappenplanId = currentSubvraag.stappenplan_id;
    const isStillCurrentSubvraag = () =>
      currentSubvraagRef.current?.id === requestSubvraagId &&
      currentSubvraagRef.current?.stappenplan_id === requestStappenplanId;

    try {
      setFetchError(false);
      setFetchErrorMessage(undefined);
      const result = await api.getStappenplan(
        requestStappenplanId,
        shareToken
      );
      if (!isStillCurrentSubvraag()) return false;

      if (result === "rate_limited") {
        setRateLimited(true);
        return false;
      }

      if (result === false) {
        markCurrentSubvraagAccess(requestSubvraagId, false);
        setPaymentRequired(true);
        return false;
      }

      setCurrentStappenplan(result);
      markCurrentSubvraagAccess(requestSubvraagId, true);
      setPaymentRequired(false);
      return true;
    } catch (err) {
      if (!isStillCurrentSubvraag()) return false;
      setCurrentStappenplan(undefined);
      setFetchErrorMessage(getFetchErrorMessage(err));
      setFetchError(true);
      return false;
    }
  }, [
    api,
    currentVraag?.code,
    currentSubvraag,
    currentStappenplan?.id,
    markCurrentSubvraagAccess,
    setCurrentStappenplan,
    setFetchError,
    setFetchErrorMessage,
    setPaymentRequired,
    setRateLimited,
    shareToken
  ]);
}
